(() => {
  'use strict';
  const managed = ['JEJE-GOLD10', 'JEJE-SILVER5', 'JEJE-PRE20', 'PRE20'];
  const nativeTitles = ['Gold Member Discount', 'Silver Member Discount', 'Pre-order 20% OFF'];
  const upper = value => String(value || '').trim().toUpperCase();
  const unique = values => [...new Set(values.map(upper).filter(Boolean))];
  const isManaged = value => managed.includes(upper(value));
  const applications = cart => [
    ...(cart.cart_level_discount_applications || []),
    ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || [])
      .map(allocation => allocation.discount_application))
  ].filter(Boolean);
  const codes = cart => unique([
    ...(cart.discount_codes || []).map(discount => discount.code),
    ...applications(cart).filter(discount => discount.type === 'discount_code').map(discount => discount.title)
  ]);
  const priceApplications = cart => applications(cart).filter(discount => discount.target_type !== 'shipping_line');
  const codeApplied = (cart, code) => applications(cart).some(discount =>
    upper(discount.title) === upper(code)) || (cart.discount_codes || []).some(discount =>
    upper(discount.code) === upper(code) && discount.applicable === true);
  const conflict = cart => {
    const active = priceApplications(cart);
    const external = codes(cart).filter(code => !isManaged(code));
    return active.some(discount => !isManaged(discount.title) &&
      (discount.type === 'discount_code' || external.includes(upper(discount.title)))) &&
      active.some(discount => isManaged(discount.title) || nativeTitles.includes(discount.title));
  };
  const same = (a, b) => JSON.stringify(unique(a).sort()) === JSON.stringify(unique(b).sort());
  const candidates = (offers, existing) => unique([...offers, ...existing.filter(code => !isManaged(code))]);
  if (typeof module !== 'undefined' && module.exports) module.exports = { applications, codes, codeApplied, conflict, same, candidates };
  if (typeof document === 'undefined') return;

  const root = window.Shopify?.routes?.root || '/';
  const blocks = () => [...document.querySelectorAll('[data-jeje-choice]')];
  const primary = () => document.querySelector('[data-jeje-choice="cart"]') || blocks()[0];
  const config = () => {
    const node = primary()?.querySelector('[data-jeje-choice-config]');
    return node ? JSON.parse(node.textContent) : null;
  };
  let busy = false;
  let scheduled;
  let lastSignature = '';
  let requestRevision = 0;
  const text = key => config()?.text[key] || '';
  const announce = (message, error = false) => blocks().forEach(block => {
    const status = block.querySelector('[data-jeje-status]');
    if (status) { status.textContent = message; status.classList.toggle('jeje-choice__error', error); }
  });
  const setBusy = value => {
    busy = value;
    blocks().forEach(block => {
      block.setAttribute('aria-busy', String(value));
      block.querySelectorAll('button').forEach(button => { button.disabled = value; });
    });
    document.querySelectorAll('#cartform [name="checkout"]').forEach(button => { button.disabled = value; });
  };
  const request = async (path, payload) => {
    const response = await fetch(root + path, {
      method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
      ...(payload ? { body: JSON.stringify(payload) } : {})
    });
    if (!response.ok) throw new Error(text('network_error'));
    return response.json();
  };
  const refresh = async () => {
    const forms = [...document.querySelectorAll('cart-form')];
    const ids = [...new Set(forms.map(form => form.dataset.sectionId).filter(Boolean))];
    if (!ids.length) return;
    const sectionUrl = new URL(window.location.href);
    sectionUrl.hash = '';
    sectionUrl.searchParams.set('sections', ids.join(','));
    const response = await fetch(sectionUrl, { cache: 'no-store' });
    if (!response.ok) throw new Error(text('network_error'));
    const sections = await response.json();
    forms.forEach(form => {
      const html = sections[form.dataset.sectionId];
      if (!html) throw new Error(text('network_error'));
      const fragment = document.createElement('div');
      fragment.innerHTML = html;
      window.theme.mergeNodes(fragment, form);
      form.classList.remove('cart-form--refreshing');
      form.querySelectorAll('.merge-item-refreshing').forEach(item => item.classList.remove('merge-item-refreshing'));
    });
  };
  const update = (discountCodes, entered = '') => request('cart/update.js', {
    discount: unique(discountCodes).join(','),
    attributes: { jeje_discount_mode: 'best', jeje_entered_code: entered }
  });
  const outcome = cart => {
    const entered = cart.attributes?.jeje_entered_code;
    announce(entered ? text(codeApplied(cart, entered) ? 'code_applied' : 'code_not_applied') : '');
  };
  const transition = async entered => {
    if (busy) return;
    setBusy(true);
    announce(text('updating'));
    let snapshot;
    try {
      snapshot = await request('cart.js');
      if (!snapshot.item_count) { await refresh(); return; }
      const wanted = candidates(config().offers, entered ? [entered] : []);
      const result = await update(wanted, entered);
      if (conflict(result)) {
        await update(codes(snapshot), snapshot.attributes?.jeje_entered_code || '');
        await refresh();
        announce(text('combination_blocked'), true);
      } else if (entered && result.total_price > snapshot.total_price) {
        await update(codes(snapshot), snapshot.attributes?.jeje_entered_code || '');
        await refresh();
        announce(text('previous_kept'));
      } else {
        await refresh();
        if (entered) outcome(result);
        else announce(text('restored'));
      }
      lastSignature = '';
    } catch (error) {
      lastSignature = '';
      announce(error.message || text('network_error'), true);
    } finally { setBusy(false); }
  };
  const sync = async () => {
    if (busy) return false;
    const settings = config();
    setBusy(true);
    const revision = requestRevision;
    try {
      let cart = await request('cart.js');
      if (revision !== requestRevision) { lastSignature = ''; schedule(); return false; }
      if (!cart.item_count) {
        if (codes(cart).length || cart.attributes?.jeje_entered_code || cart.attributes?.jeje_discount_mode !== 'best') await update([]);
        return true;
      }
      if (!settings) return true;
      const existing = codes(cart);
      const external = existing.filter(code => !isManaged(code));
      const entered = external.includes(upper(cart.attributes?.jeje_entered_code))
        ? upper(cart.attributes.jeje_entered_code) : external[0] || '';
      const wanted = candidates(settings.offers, external);
      if (wanted.length > 5) throw new Error(text('too_many_codes'));
      if (!same(existing, wanted) || cart.attributes?.jeje_discount_mode !== 'best' ||
          (cart.attributes?.jeje_entered_code || '') !== entered) {
        cart = await update(wanted, entered);
        await refresh();
      }
      if (conflict(cart)) {
        await update(settings.offers);
        await refresh();
        announce(text('combination_blocked'), true);
        return false;
      }
      outcome(cart);
      return true;
    } catch (error) {
      lastSignature = '';
      announce(error.message || text('network_error'), true);
      return false;
    } finally { setBusy(false); }
  };
  const schedule = () => {
    clearTimeout(scheduled);
    scheduled = setTimeout(() => {
      if (busy) { schedule(); return; }
      const settings = config();
      const signature = JSON.stringify([settings?.offers, settings?.mode, settings?.entered, settings?.items, settings?.applied]);
      if (signature === lastSignature) return;
      lastSignature = signature;
      sync();
    }, 180);
  };
  document.addEventListener('on:cart:change', () => { requestRevision += 1; lastSignature = ''; });
  document.addEventListener('on:cart:after-merge', schedule);
  window.addEventListener('pageshow', () => { lastSignature = ''; schedule(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { lastSignature = ''; schedule(); } });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-jeje-action]');
    if (!button) return;
    event.preventDefault();
    if (busy) return;
    if (button.dataset.jejeAction === 'apply') {
      const input = button.closest('[data-jeje-choice]').querySelector('[data-jeje-code]');
      const code = upper(input.value);
      if (!code || code.includes(',') || isManaged(code)) { announce(text('enter_reward'), true); input.focus(); return; }
      transition(code);
    } else if (button.dataset.jejeAction === 'restore') transition('');
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-jeje-code]')) {
      event.preventDefault();
      event.target.closest('[data-jeje-choice]').querySelector('[data-jeje-action="apply"]').click();
    }
  });
  document.addEventListener('submit', async event => {
    if (event.target.id !== 'cartform' || event.submitter?.name !== 'checkout') return;
    const terms = event.target.querySelector('terms-agreement input[type="checkbox"]');
    if (terms && !terms.checked) return;
    event.preventDefault();
    if (busy) return;
    clearTimeout(scheduled);
    if (!await sync()) { primary()?.scrollIntoView({ block: 'center', behavior: 'smooth' }); return; }
    setBusy(true);
    const hidden = document.createElement('input');
    hidden.type = 'hidden'; hidden.name = 'checkout'; hidden.value = 'Checkout';
    event.target.appendChild(hidden);
    HTMLFormElement.prototype.submit.call(event.target);
  }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
})();
